import { useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'

import { useSites } from 'store'
import { useSort, useFilters } from './store'
import { deserializeErrors } from '../../apollo-client'

export interface IEventSsid {
  id: string
  label: string
  name: string
  description: string | null
  enabled: boolean
  start_date: string
  end_date: string
  start_time: string
  end_time: string
  utc_offset: string
  passphrase: string
  error: string | null
  created_at: string
  created_by: {
    email: string
  }
  updated_by: {
    email: string
  }
}

const OPERATION_SSIDS = gql`
  query cmtSsids($sfId: ID!, $status: String, $sort_by: String, $sort_direction: String, $page: Int, $limit: Int) {
    ssids: cmtSsids(site_sf_id: $sfId, status: $status, sort_by: $sort_by, sort_direction: $sort_direction, page: $page, limit: $limit) {
      data {
        id
        label
        name
        description
        enabled
        start_date
        end_date
        start_time
        end_time
        utc_offset
        passphrase
        error
        created_at
        created_by {
          email
        }
        updated_by {
          email
        }
      }
      pagination {
        current_page
        next_page
        has_next_page
        total
      }
    }
  }
`

const OPERATION_SSID = gql`
  query cmtSsid($id: ID!) {
    ssid: cmtSsid(id: $id) {
      id
      label
      name
      description
      enabled
      start_date
      end_date
      start_time
      end_time
      utc_offset
      passphrase
      error
      created_at
      created_by {
        email
      }
      updated_by {
        email
      }
    }
  }
`

const PAGE_SIZE = 10

const useList = () => {
  const isLoadingMore = useRef<boolean>(false)
  const [filters] = useFilters()
  const [{ selected: sitesSelected }] = useSites()
  const [sort] = useSort()
  const sfId: string = useMemo(
    () => sitesSelected[0]?.sf_id as string,
    [sitesSelected]
  )

  const queryVariables = useMemo(() => ({
    sfId,
    status: filters.status,
    sort_by: sort.by,
    sort_direction: sort.direction,
    page: 1,
    limit: PAGE_SIZE
  }), [sfId, filters.status, sort.by, sort.direction])

  const { loading, data, previousData, error, fetchMore, networkStatus } = useQuery(OPERATION_SSIDS, {
    notifyOnNetworkStatusChange: true,
    variables: queryVariables,
    skip: !sfId
  })

  const effectiveData = data || previousData

  const items = useMemo(() => {
    return effectiveData?.ssids?.data || []
  }, [effectiveData])

  return {
    // @ts-expect-error JSX
    error: error ? deserializeErrors(error)?.replaceAll('Errors::', '') : null,
    loading: loading && !effectiveData && networkStatus !== 3,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: !loading && !isLoadingMore.current && data?.ssids?.pagination?.has_next_page,
    fetchNextPage: async () => {
      if (networkStatus === 3 || isLoadingMore.current) {
        return
      }

      const nextPage = data?.ssids?.pagination?.next_page
      if (!nextPage) {
        return
      }

      try {
        isLoadingMore.current = true

        await fetchMore({
          variables: {
            page: nextPage,
            limit: PAGE_SIZE
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev
            if (!prev.ssids || !fetchMoreResult.ssids) return prev

            const existingIds = new Set(prev.ssids.data.map(item => item.id))
            const newData = fetchMoreResult.ssids.data.filter(item => !existingIds.has(item.id))

            return {
              ssids: {
                data: [...prev.ssids.data, ...newData],
                pagination: fetchMoreResult.ssids.pagination
              }
            }
          }
        })
      } catch (e) {
        console.error('Error fetching next page:', e)
      } finally {
        isLoadingMore.current = false
      }
    },
    pagination: data?.ssids?.pagination || {},
    items: items as IEventSsid[],
  }
}

export const useDetail = () => {
  const { id } = useParams()
  const { loading, error, data } = useQuery(OPERATION_SSID, {
    variables: {
      id
    },
    skip: !id
  })
  return {
    loading,
    error: error?.message || null,
    item: data?.ssid
  }
}

export default useList